import type { NormalizedApiError } from "~/store/middleware/normalizeApiError";

const STATIC_MESSAGES: Record<string, string> = {
	AUTH_NOT_AUTHORIZED: "You don't have permission for this action.",
	AUTH_NO_TENANT: "Your session isn't linked to a tenant. Please sign out and back in.",
	AUTH_READONLY_TOKEN: "Your access is read-only, so this action isn't allowed.",
	AUTH_TENANT_MISMATCH: "Your session doesn't match this tenant. Please sign out and back in.",
	HC0051: "The page requested is too large. Please contact support if this persists."
};

// Codes owned by a dedicated surface (baseApi token refresh/redirect, TrialLimitDialog) —
// a middleware snack would be redundant.
const SILENT_CODES = new Set<string>(["AUTH_NOT_AUTHENTICATED", "TRIAL_LIMIT_REACHED", "UNAUTHENTICATED"]);

// HC0011 is the validation umbrella — surface the inner GraphQL message instead
// of the generic code.
const USE_INNER_MESSAGE_CODES = new Set<string>(["HC0011"]);

const isNetworkMessage = (message: string | null | undefined): boolean => {
	if (!message) return false;
	return (
		message.includes("Network request failed") ||
		message.includes("Failed to fetch") ||
		message.includes("fetch is not defined")
	);
};

const firstCode = (normalized: NormalizedApiError): string | null => {
	const first = normalized.graphqlErrors?.[0];
	const extensions = first?.extensions;
	if (extensions && typeof extensions === "object" && "code" in extensions) {
		const code = (extensions as { code?: unknown }).code;
		if (typeof code === "string" && code.length > 0) return code;
	}
	return null;
};

// Status-only 401s — baseApi handles refresh/redirect.
// A snack here would flash before the redirect.
const isUnauthorizedStatus = (status: NormalizedApiError["status"]): boolean => {
	if (status === 401) return true;
	if (typeof status === "string" && (status === "401" || status === "UNAUTHORIZED")) return true;
	return false;
};

const mapGraphqlErrorToMessage = (normalized: NormalizedApiError): string | null => {
	// Network failures: the offline UI / retry layer owns user feedback.
	if (isNetworkMessage(normalized.message) || normalized.status === "FETCH_ERROR") {
		return null;
	}

	if (isUnauthorizedStatus(normalized.status)) return null;

	const code = firstCode(normalized);

	if (code && SILENT_CODES.has(code)) return null;
	if (code && STATIC_MESSAGES[code]) return STATIC_MESSAGES[code];

	if (code && USE_INNER_MESSAGE_CODES.has(code)) {
		const inner = normalized.graphqlErrors?.[0]?.message;
		if (inner && inner.length > 0) return inner;
		return "The information you submitted is invalid. Please review and try again.";
	}

	// Unmapped codes stay silent at middleware level — helper-owned snacks retain
	// ownership and won't double-fire.
	return null;
};

export default mapGraphqlErrorToMessage;
